import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import DataTable, { DataTableColumn } from '../../../ui/DataTable';
import RowActionMenu, { RowActionMenuItem } from '../../../ui/RowActionMenu';
import { PitchBodyTemplate, PitchBodyField } from '../../../../types/email';
import { handleError } from '../../../../utils/errorHandler';
import pitchBodiesApi from '../../../../repositories/pitchBodies';
import PitchBodyModal from './PitchBodyModal';
import { useAppSelector } from '../../../../hooks/redux';
import SettingsPanel from '../../../ui/SettingsPanel';
import Button from '../../../ui/Button';
import { swalColors } from '../../../../utils/swal';

const PitchBodies: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';
  const [templates, setTemplates] = useState<PitchBodyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PitchBodyTemplate | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await pitchBodiesApi.getAll();
      if (res?.data?.success) {
        setTemplates(res.data.templates ?? []);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (template: PitchBodyTemplate) => {
    if (template.isGlobal && !isAdmin) {
      Swal.fire({
        title: 'Cannot Edit',
        text: 'Global pitch bodies cannot be edited. You can create your own instead.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
      return;
    }
    setEditing(template);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const template = templates.find((t) => t.id === id);

    if (template?.isGlobal && !isAdmin) {
      Swal.fire({
        title: 'Cannot Delete',
        text: 'Global pitch bodies cannot be deleted.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${template?.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalColors.danger,
      confirmButtonText: 'Yes, delete',
    });

    if (result.isConfirmed) {
      try {
        const res = await pitchBodiesApi.delete(id);
        if (res?.data?.success) {
          await fetchTemplates();
          Swal.fire({ title: 'Deleted!', icon: 'success', confirmButtonText: 'OK' });
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleSave = async (name: string, fields: PitchBodyField[]) => {
    try {
      if (editing) {
        const res = await pitchBodiesApi.update(editing.id, { name, fields });
        if (!res?.data?.success) throw new Error(res?.data?.message ?? 'Failed to update');
        setModalOpen(false);
        await fetchTemplates();
        Swal.fire({ title: 'Updated!', icon: 'success', confirmButtonText: 'OK' });
      } else {
        const res = await pitchBodiesApi.create({ name, fields });
        if (!res?.data?.success) throw new Error(res?.data?.message ?? 'Failed to create');
        setModalOpen(false);
        await fetchTemplates();
        Swal.fire({ title: 'Created!', icon: 'success', confirmButtonText: 'OK' });
      }
    } catch (error) {
      handleError(error);
    }
  };

  const getEnabledFieldLabels = (fields: PitchBodyField[]) =>
    fields
      .filter((f) => f.enabled)
      .sort((a, b) => a.order - b.order)
      .map((f) => f.label)
      .join(', ');

  const bodyColumns: DataTableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      // Global is an ownership distinction, not a status, so it rides alongside
      // the name as a tag rather than taking a column of its own. Same token the
      // Templates tab uses for its Global pill.
      cell: (t) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{t.name}</span>
          {t.isGlobal && (
            <span className="inline-flex shrink-0 items-center px-2.5 py-0.5 text-xs font-medium border rounded-full bg-primary-50 text-primary-800 border-primary-200">
              Global
            </span>
          )}
        </div>
      ),
    },
    {
      key: "fields",
      header: "Fields",
      cell: (t) => (
        <div className="max-w-xs truncate text-neutral-600">
          {getEnabledFieldLabels(t.fields) || (
            <span className="italic text-neutral-400">No fields enabled</span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (template) => (
        <RowActionMenu
          ariaLabel="Pitch body actions"
          items={[
            { label: "Edit", onClick: () => handleEdit(template) },
            { label: "Delete", danger: true, onClick: () => handleDelete(template.id) },
          ]}
        />
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Pitch Bodies"
      description="Define which pitch fields appear in your emails and in what order."
      actions={
        <Button
          onClick={handleCreate}
          className="shrink-0"
        >
          Create Pitch Body
        </Button>
      }
    >

      {/* Table */}
      <DataTable
        columns={bodyColumns}
        rows={templates}
        getRowKey={(t: any) => t.id}
        loading={loading}
        emptyMessage="No pitch body templates yet. Create one to get started."
      />

      <PitchBodyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        template={editing}
      />
    </SettingsPanel>
  );
};

export default PitchBodies;
